import type { IntegrationDataModelSyncSettings } from '@commenda/api';
import {
  DatePicker,
  Icons,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import type {
  RootfiDataModelV4,
  RootFiWorkspaceSyncFrequencyV4,
} from '@commenda/types';
import { cn } from '@commenda/utils';
import { useEffect, useState } from 'react';

import {
  MIXED_SYNC_VALUE,
  ROOTFI_SUPPORTED_SYNC_FREQUENCIES,
  RootFiDataModelSyncHelper,
  RootFiGlobalSyncSettingsPatch,
} from '../helpers/RootFiDataModelSyncHelper';

interface RootFiSyncCadenceInputProps {
  /** `MIXED_SYNC_VALUE` renders a read-only "Custom" option until the user picks one. */
  frequency: RootFiWorkspaceSyncFrequencyV4 | typeof MIXED_SYNC_VALUE;
  interval: number | typeof MIXED_SYNC_VALUE;
  onChange: (
    patch: Pick<RootFiGlobalSyncSettingsPatch, 'frequency' | 'interval'>
  ) => void;
  size?: 'sm' | 'md';
}

/** "Every [interval] [unit]" control. */
const RootFiSyncCadenceInput = ({
  frequency,
  interval,
  onChange,
  size = 'sm',
}: RootFiSyncCadenceInputProps) => {
  const isCustomFrequency =
    frequency === MIXED_SYNC_VALUE ||
    !RootFiDataModelSyncHelper.isSupportedFrequency(frequency);
  const heightClass = size === 'md' ? 'h-11' : 'h-9';
  const textClass = size === 'md' ? 'txt-medium-regular' : 'txt-small-regular';

  return (
    <div className="flex shrink-0 items-center gap-sm">
      <span className="txt-small-regular text-text-secondary">Every</span>
      <Select
        value={
          isCustomFrequency || interval === MIXED_SYNC_VALUE
            ? undefined
            : `${interval}`
        }
        disabled={isCustomFrequency}
        onValueChange={(next) => {
          const nextInterval = Number(next);
          if (!Number.isInteger(nextInterval)) return;
          onChange({ interval: nextInterval });
        }}
      >
        <SelectTrigger
          className={cn(heightClass, textClass, 'w-[72px] shrink-0 px-md')}
        >
          <SelectValue placeholder="–" />
        </SelectTrigger>
        <SelectContent>
          {!isCustomFrequency &&
            RootFiDataModelSyncHelper.getIntervalOptions(frequency).map(
              (option) => (
                <SelectItem key={option} value={`${option}`}>
                  {option}
                </SelectItem>
              )
            )}
        </SelectContent>
      </Select>
      <Select
        value={isCustomFrequency ? MIXED_SYNC_VALUE : frequency}
        onValueChange={(next) => {
          if (!RootFiDataModelSyncHelper.isSupportedFrequency(next)) return;
          onChange(
            next === frequency
              ? { frequency: next }
              : { frequency: next, interval: 1 }
          );
        }}
      >
        <SelectTrigger
          className={cn(heightClass, textClass, 'w-[120px] shrink-0 px-md')}
        >
          <SelectValue placeholder="Select unit" />
        </SelectTrigger>
        <SelectContent>
          {isCustomFrequency && (
            <SelectItem value={MIXED_SYNC_VALUE} disabled>
              {frequency === MIXED_SYNC_VALUE
                ? 'Custom'
                : RootFiDataModelSyncHelper.getFrequencyUnitLabel(frequency)}
            </SelectItem>
          )}
          {ROOTFI_SUPPORTED_SYNC_FREQUENCIES.map((option) => (
            <SelectItem key={option} value={option}>
              {RootFiDataModelSyncHelper.getFrequencyUnitLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

interface RootFiSyncConfigurationFieldsProps {
  settings: IntegrationDataModelSyncSettings[];
  onChange: (settings: IntegrationDataModelSyncSettings[]) => void;
}

/** Global frequency and sync-from controls with per-data-model overrides. */
const RootFiSyncConfigurationFields = ({
  settings,
  onChange,
}: RootFiSyncConfigurationFieldsProps) => {
  const globalFrequency =
    RootFiDataModelSyncHelper.getGlobalFrequency(settings);
  const globalInterval = RootFiDataModelSyncHelper.getGlobalInterval(settings);
  const globalSyncFrom = RootFiDataModelSyncHelper.getGlobalSyncFrom(settings);
  const hasCustomSettings =
    globalFrequency === MIXED_SYNC_VALUE ||
    globalInterval === MIXED_SYNC_VALUE ||
    globalSyncFrom === MIXED_SYNC_VALUE;
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  useEffect(() => {
    if (hasCustomSettings) setIsAdvancedExpanded(true);
  }, [hasCustomSettings]);

  const updateModel = (
    dataModel: RootfiDataModelV4,
    patch: Partial<
      Pick<
        IntegrationDataModelSyncSettings,
        'frequency' | 'interval' | 'syncFrom'
      >
    >
  ) =>
    onChange(
      settings.map((item) =>
        item.dataModel === dataModel ? { ...item, ...patch } : item
      )
    );

  return (
    <div className="flex min-w-0 flex-col gap-4xl">
      <div className="flex flex-col gap-xxs">
        <span className="txt-small-bold text-text-primary">Sync frequency</span>
        <p className="txt-small-regular text-text-tertiary">
          Choose how often Commenda checks this integration for new or updated
          records.
        </p>
        <div className="pt-md">
          <RootFiSyncCadenceInput
            frequency={globalFrequency}
            interval={globalInterval}
            size="md"
            onChange={(patch) =>
              onChange(
                RootFiDataModelSyncHelper.applyGlobalSettings(settings, patch)
              )
            }
          />
        </div>
      </div>

      <div className="flex flex-col gap-xxs">
        <span className="txt-small-bold text-text-primary">Sync from</span>
        <p className="txt-small-regular text-text-tertiary">
          Only records on or after this date will be synced.
        </p>
        <div className="max-w-[240px] pt-md">
          <DatePicker
            date={
              globalSyncFrom === MIXED_SYNC_VALUE ? '' : (globalSyncFrom ?? '')
            }
            setDate={(date) =>
              onChange(
                RootFiDataModelSyncHelper.applyGlobalSettings(settings, {
                  syncFrom: date || null,
                })
              )
            }
            _triggerClassName="txt-medium-regular text-text-secondary h-11 px-md"
            placeholder={
              globalSyncFrom === MIXED_SYNC_VALUE
                ? 'Custom per data type'
                : 'All history'
            }
          />
        </div>
      </div>

      <div className="flex flex-col gap-md pb-md">
        <button
          type="button"
          className="flex items-center gap-sm self-start appearance-none focus:outline-none"
          onClick={() => setIsAdvancedExpanded((expanded) => !expanded)}
          aria-expanded={isAdvancedExpanded}
        >
          <span className="txt-small-medium text-text-secondary">
            {isAdvancedExpanded ? 'Hide advanced' : 'Show advanced'}
          </span>
          <Icons.ChevronDown
            className={cn(
              'size-4 text-text-quaternary transition-transform',
              isAdvancedExpanded && 'rotate-180'
            )}
          />
        </button>

        <p className="txt-small-regular text-text-tertiary">
          Set a different frequency and start date for each data type.
        </p>

        {isAdvancedExpanded ? (
          <div className="flex flex-col divide-y divide-border-secondary rounded-md border border-border-primary px-lg pt-md">
            {settings.map((item) => (
              <div
                key={item.dataModel}
                className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-md py-md"
              >
                <span className="txt-small-medium truncate text-text-primary">
                  {item.label}
                </span>
                <RootFiSyncCadenceInput
                  frequency={item.frequency}
                  interval={item.interval}
                  onChange={(patch) => updateModel(item.dataModel, patch)}
                />
                <div className="w-[150px] shrink-0">
                  <DatePicker
                    date={item.syncFrom ?? ''}
                    setDate={(date) =>
                      updateModel(item.dataModel, {
                        syncFrom: date || null,
                      })
                    }
                    _triggerClassName="txt-small-regular text-text-secondary h-9 px-md"
                    placeholder="All history"
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export { RootFiSyncConfigurationFields };
